import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  House,
  CalendarBlank,
  ListBullets,
  UsersThree,
  Scissors,
  Sparkle,
  ChartBar,
  GearSix,
  SignOut,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import SophiePanel from "./SophiePanel";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, end: true },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank },
  { to: "/bookings", label: "Bookings", icon: ListBullets },
  { to: "/customers", label: "Customers", icon: UsersThree },
  { to: "/stylists", label: "Stylists", icon: Scissors },
  { to: "/services", label: "Services", icon: Sparkle },
  { to: "/analytics", label: "Analytics", icon: ChartBar },
  { to: "/settings", label: "Settings", icon: GearSix, adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col w-[280px] shrink-0 border-r border-border bg-[#F9F8F6]"
        data-testid="sidebar"
      >
        <div className="px-6 pt-7 pb-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Scissors size={18} weight="duotone" />
            </div>
            <div>
              <div className="font-heading text-lg leading-none tracking-tight">Sophie</div>
              <div className="text-[11px] text-muted-foreground tracking-[0.2em] uppercase mt-1">Salon Suite</div>
            </div>
          </Link>
        </div>

        <nav className="px-3 flex-1 space-y-0.5">
          {NAV.filter((i) => !i.adminOnly || user?.role === "admin").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-secondary"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={18} weight={isActive ? "fill" : "duotone"} />
                  <span className="font-medium">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="m-3 p-3 rounded-md border border-border bg-white" data-testid="user-card">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-secondary text-foreground text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate capitalize">{user?.role}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              data-testid="logout-btn"
              aria-label="Logout"
              className="hover:bg-secondary"
            >
              <SignOut size={16} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="glass-header sticky top-0 z-20 px-6 md:px-10 py-4" data-testid="topbar">
          <div className="flex items-center justify-between">
            <div className="md:hidden flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                <Scissors size={16} weight="duotone" />
              </div>
              <span className="font-heading text-lg">Sophie</span>
            </div>
            <div className="hidden md:block">
              <div className="overline">Operator</div>
              <div className="text-sm font-medium">{user?.email}</div>
            </div>
            <div className="md:hidden">
              <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn-mobile">
                <SignOut size={16} className="mr-1" /> Logout
              </Button>
            </div>
          </div>
        </header>
        <main className="flex-1 px-6 md:px-10 py-8 max-w-[1400px] w-full mx-auto" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      <SophiePanel />
    </div>
  );
}
